import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { AlertCircle, RefreshCw, XIcon } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"

import { createRequestWithdraw, savePaymentPolicyAgreement, sendCodeVerifyWithdraw } from "@/api"
import { Alert, AlertDescription } from "@/components/ui"
import { Button } from "@/components/ui"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui"
import { EValidateUser, PolicyStatus } from "@/modules/helpers/enum"
import { getDeviceName } from "@/modules/helpers/hooks"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { formatPhoneNumber } from "@/modules/helpers/utils"

// Types
interface ConfirmOTPWithdrawProps {
  visible: boolean
  onClose: () => void
  onSuccess: () => void
  data: {
    bankId: string
    amount: number
  }
  shouldSavePolicy?: boolean
}

interface FormData {
  otp: string
  retry: boolean
}

// Constants
const OTP_LENGTH = 6
const OTP_EXPIRY_SECONDS = 90
const OTP_STORAGE_KEY = "@expired_time_otp_withdraw"

// Custom hook for OTP form logic
const useOTPForm = () => {
  return useForm<FormData>({
    defaultValues: {
      otp: "",
      retry: true
    }
  })
}

// Custom hook for withdraw confirmation
const useWithdrawConfirmation = (
  data: ConfirmOTPWithdrawProps["data"],
  onSuccess: () => void,
  shouldSavePolicy?: boolean
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (otp: string) =>
      createRequestWithdraw({
        bankAccountId: data.bankId,
        amount: data.amount,
        code: otp
      }),
    mutationKey: ["confirm_otp_withdraw"],
    onError(err: any) {
      const errorData = err?.response?.data
      const errorType = errorData?.error.type

      let message = "Có lỗi xảy ra, thử lại sau."

      if (errorType?.includes(EValidateUser.OTP_NOT_MATCHED)) {
        message = "Mã xác minh không đúng."
      } else if (errorType?.includes(EValidateUser.OTP_EXPIRED)) {
        message = "Mã xác minh hết hạn."
      } else if (errorType?.includes(EValidateUser.BALANCE_NOT_ENOUGH)) {
        message = "Số dư không đủ."
      }

      return { message }
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ["transaction_list"] })

      // Save payment policy agreement after successful withdraw
      if (shouldSavePolicy) {
        try {
          await savePaymentPolicyAgreement({
            deviceName: getDeviceName(),
            type: PolicyStatus.PAYMENT
          })
          // Invalidate policy query to update the UI
          await queryClient.invalidateQueries({ queryKey: ["payment_policy_agreement"] })
        } catch (error) {
          console.error("Failed to save payment policy agreement:", error)
        }
      }

      onSuccess()
    }
  })
}

// Custom hook for OTP resend
const useOTPResend = (onResendSuccess?: () => void) => {
  return useMutation({
    mutationFn: () => sendCodeVerifyWithdraw(),
    mutationKey: ["resend_otp_withdraw"],
    onSuccess() {
      const expiryTime = dayjs(Date.now()).add(OTP_EXPIRY_SECONDS, "seconds").toString()
      localStorage.setItem(OTP_STORAGE_KEY, expiryTime)
      onResendSuccess?.()
    },
    onError() {
      return { message: "Không thể gửi lại OTP" }
    }
  })
}

// Custom hook for countdown timer
const useCountdown = () => {
  const [seconds, setSeconds] = useState(0)
  const [isActive, setIsActive] = useState(false)

  // Load remaining time from localStorage on mount
  useEffect(() => {
    const savedExpiryTime = localStorage.getItem(OTP_STORAGE_KEY)
    if (savedExpiryTime) {
      const expiryTime = dayjs(savedExpiryTime)
      const now = dayjs()
      const remainingSeconds = expiryTime.diff(now, "second")

      if (remainingSeconds > 0) {
        setSeconds(remainingSeconds)
        setIsActive(true)
      } else {
        setSeconds(0)
        setIsActive(false)
        localStorage.removeItem(OTP_STORAGE_KEY)
      }
    }
  }, [])

  const start = useCallback(() => {
    const savedExpiryTime = localStorage.getItem(OTP_STORAGE_KEY)
    if (savedExpiryTime) {
      const expiryTime = dayjs(savedExpiryTime)
      const now = dayjs()
      const remainingSeconds = expiryTime.diff(now, "second")
      setSeconds(remainingSeconds)
      setIsActive(true)
    }
  }, [])

  const reset = useCallback(() => {
    const savedExpiryTime = localStorage.getItem(OTP_STORAGE_KEY)
    if (savedExpiryTime) {
      const expiryTime = dayjs(savedExpiryTime)
      const now = dayjs()
      const remainingSeconds = expiryTime.diff(now, "second")
      setSeconds(remainingSeconds)
      setIsActive(true)
    }
  }, [])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isActive) {
      interval = setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            setIsActive(false)
            localStorage.removeItem(OTP_STORAGE_KEY)
            return 0
          }
          return prevSeconds - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isActive])

  return { seconds, isActive, start, reset }
}

// Component for OTP input field
const OTPInput: React.FC<{
  control: any
  onSubmit: (data: FormData) => void
}> = ({ control, onSubmit }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, value: string, field: any) => {
    // Only allow digits
    const digit = value.replace(/[^0-9]/g, "")

    if (digit.length > 1) {
      // If pasting multiple digits, distribute them across boxes
      const digits = digit.slice(0, OTP_LENGTH).split("")
      const currentOtp = Array.from({ length: OTP_LENGTH }, (_, i) => (field.value || "")[i] || "")

      digits.forEach((d: string, i: number) => {
        const targetIndex = index + i
        if (targetIndex < OTP_LENGTH) {
          currentOtp[targetIndex] = d
        }
      })

      field.onChange(currentOtp.join(""))

      // Focus on the next empty box or last box
      const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1)
      inputRefs.current[nextIndex]?.focus()
    } else {
      // Single digit input
      const currentOtp = Array.from({ length: OTP_LENGTH }, (_, i) => (field.value || "")[i] || "")
      currentOtp[index] = digit
      field.onChange(currentOtp.join(""))

      // Auto-focus next input
      if (digit && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>, field: any) => {
    if (e.key === "Backspace") {
      const currentOtp = Array.from({ length: OTP_LENGTH }, (_, i) => (field.value || "")[i] || "")

      if (!currentOtp[index] && index > 0) {
        // If current box is empty, move to previous box
        inputRefs.current[index - 1]?.focus()
      }

      currentOtp[index] = ""
      field.onChange(currentOtp.join(""))
    } else if (e.key === "Enter" && (field.value || "").replace(/\s/g, "").length === OTP_LENGTH) {
      onSubmit({ otp: field.value, retry: true })
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  return (
    <Controller
      name="otp"
      control={control}
      render={({ field, fieldState }) => {
        const otpValue = field.value || ""
        const otpDigits = Array.from({ length: OTP_LENGTH }, (_, i) => otpValue[i] || "")

        return (
          <div className="space-y-2">
            <div className="flex gap-1 justify-center">
              {otpDigits.map((digit: string, index: number) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value, field)}
                  onKeyDown={(e) => handleKeyDown(index, e, field)}
                  className="w-[23%] aspect-square text-center text-2xl font-medium border border-gray-300 rounded-[30%] focus:border-primary focus:outline-none transition-colors bg-white"
                />
              ))}
            </div>
            {fieldState.error && (
              <p className="text-sm text-red-500 text-center">{fieldState.error.message}</p>
            )}
          </div>
        )
      }}
    />
  )
}

// Component for error display
const ErrorAlert: React.FC<{
  error: string | null
}> = ({ error }) => {
  if (!error) return null

  return (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  )
}

// Component for resend button with countdown
const ResendButton: React.FC<{
  countdown: { seconds: number; isActive: boolean; start: () => void; reset: () => void }
  isPending: boolean
  onResend: () => void
}> = ({ countdown, isPending, onResend }) => {
  const { seconds, isActive } = countdown
  const canResend = !isActive && !isPending

  const handleResend = () => {
    onResend()
    countdown.start()
  }

  return (
    <div className="flex justify-center">
      <Button
        variant="link"
        onClick={handleResend}
        disabled={!canResend}
        className="flex items-center gap-2"
      >
        {isPending && <RefreshCw className="h-4 w-4 animate-spin" />}
        {isActive ? `Gửi lại sau ${seconds}s` : isPending ? "Đang gửi..." : "Gửi lại"}
      </Button>
    </div>
  )
}

// Main component
export const ConfirmOTPWithdraw: React.FC<ConfirmOTPWithdrawProps> = ({
  visible,
  onClose,
  onSuccess,
  data,
  shouldSavePolicy
}) => {
  const [generalError, setGeneralError] = useState<string | null>(null)
  const { user } = useCurrentUser()

  const form = useOTPForm()
  const countdown = useCountdown()

  const {
    mutate: confirmWithdraw,
    error: withdrawError,
    isPending: isConfirmPending
  } = useWithdrawConfirmation(data, onSuccess, shouldSavePolicy)
  const {
    mutate: resendOTP,
    isPending: isResendPending,
    error: resendError
  } = useOTPResend(() => {
    setGeneralError(null)
    countdown.reset()
    form.clearErrors()
  })

  const handleSubmit = ({ otp }: FormData) => {
    if (otp?.length < OTP_LENGTH) {
      form.setError("otp", { message: "Nhập thiếu mã xác minh." })
      return
    }

    form.clearErrors()
    setGeneralError(null)
    confirmWithdraw(otp)
  }

  const handleResend = () => {
    setGeneralError(null)
    form.clearErrors()
    resendOTP()
    form.setValue("retry", true)
  }

  // Handle errors from mutations
  useEffect(() => {
    if (withdrawError) {
      const error = withdrawError as any
      const errorData = error?.response?.data
      const errorType = errorData?.error?.type

      let message = errorData?.error?.message

      if (errorType?.includes(EValidateUser.OTP_NOT_MATCHED)) {
        message = "Mã xác minh không đúng."
        form.setError("otp", { message })
      } else if (errorType?.includes(EValidateUser.OTP_EXPIRED)) {
        message = "Mã xác minh hết hạn."
        setGeneralError(message)
      } else if (errorType?.includes(EValidateUser.BALANCE_NOT_ENOUGH)) {
        message = "Số dư không đủ."
        setGeneralError(message)
      } else {
        setGeneralError(message)
      }
    }
  }, [withdrawError, form])

  // Handle resend errors
  useEffect(() => {
    if (resendError) {
      setGeneralError("Không thể gửi lại OTP. Vui lòng thử lại sau.")
    }
  }, [resendError])

  // Clear errors and start countdown when dialog opens
  useEffect(() => {
    if (visible) {
      form.clearErrors()
      setGeneralError(null)
      countdown.start() // Start countdown when dialog opens
    } else {
      countdown.reset() // Reset countdown when dialog closes
      form.reset()
    }
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={visible} onOpenChange={onClose} modal>
      <DialogContent showCloseButton={false} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Xác nhận OTP</DialogTitle>
          <DialogClose onClick={onClose} className="absolute top-2 right-2">
            <XIcon className="w-6 h-6 text-muted-foreground" />
          </DialogClose>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Nhập mã OTP mà chúng tôi vừa gửi đến số điện thoại{" "}
              <span className="font-medium">{formatPhoneNumber(user?.phoneNumber)}</span> để yêu cầu
              rút tiền
            </p>
          </div>

          <ErrorAlert error={generalError} />

          <OTPInput control={form.control} onSubmit={handleSubmit} />

          <ResendButton countdown={countdown} isPending={isResendPending} onResend={handleResend} />

          <Button
            className="w-full !h-12 rounded-xl bg-gradient-primary"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isConfirmPending}
          >
            {isConfirmPending ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Đang xác nhận...
              </div>
            ) : (
              "Xác nhận"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
